import { ChangeEvent, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { type AxiosError } from "axios";
import { z } from "zod";
import $api from "@/zapi-frontend/shared/api/axios";
import { loginErrorMessage } from "../const/const";
import { Link, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { LogoSvg, WarningIcon } from "@libs/zapi-ui/assets";
import { getRouteProjects, getRouteVerifyOtp } from "@/zapi-frontend/shared/const/router";
import { Input, Button } from "@libs/zapi-ui/shadcn";
import { useHistoricalNavigate } from "@/zapi-frontend/shared/hooks/useHistoricalNavigate";
import { useCookieConsentStore } from "@/zapi-frontend/shared/hooks/useCookieConsent";

const CredentialsSchema = z.object({
  email: z.string().email({ message: "Invalid email address" }),
  password: z.string()
});

type TCredentials = z.infer<typeof CredentialsSchema>;

export function SignInPage() {
  const { navigate } = useHistoricalNavigate();
  const queryClient = useQueryClient();
  const [credentials, setCredentials] = useState<TCredentials>({ email: "", password: "" });
  const [isErrorShown, setIsErrorShown] = useState(false);
  const [emailError, setEmailError] = useState<string | undefined>(undefined);
  const { search } = useLocation();
  const [showPassword, setShowPassword] = useState(false);
  const { openModal, hasGivenConsent, setUserId } = useCookieConsentStore();

  const isValidCredentials = CredentialsSchema.safeParse(credentials).success;
  const mutation = useMutation({
    mutationFn: async (credentials: TCredentials) => {
      const payload = { email: credentials.email, password: credentials.password };
      return await $api.post<{ data: { requiresTwoFactor?: boolean; twoFactorToken?: string } }>(
        "/auth/login",
        payload
      );
    },
    onSuccess: async (response) => {
      const data = response.data?.data;

      if (data?.requiresTwoFactor && data?.twoFactorToken) {
        const next = new URLSearchParams(search).get("next");
        const otpUrl = next
          ? `${getRouteVerifyOtp()}?token=${data.twoFactorToken}&next=${encodeURIComponent(next)}`
          : `${getRouteVerifyOtp()}?token=${data.twoFactorToken}`;
        navigate(otpUrl);
        return;
      }

      await queryClient.invalidateQueries({ queryKey: ["session-verify"] });
      const verifiedUser = queryClient.getQueryData<{ id: number }>(["session-verify"]);
      if (verifiedUser?.id) setUserId(verifiedUser.id);
      if (!hasGivenConsent()) openModal();

      const next = new URLSearchParams(search).get("next");

      if (next) {
        navigate(next);
        return;
      }
      navigate(getRouteProjects());
    },
    onError: (err: AxiosError<{ message: string }>) => {
      const message = err?.response?.data?.message ?? "Failed to sign in";
      toast.error(message);
      setIsErrorShown(true);
    }
  });

  async function getLoginAccess() {
    if (!isValidCredentials) return;
    try {
      await mutation.mutateAsync(credentials);
    } catch {
      return;
    }
  }

  function onChangeEmail(e: ChangeEvent<HTMLInputElement>) {
    setEmailError(undefined);
    setIsErrorShown(false);
    setCredentials((prev) => ({ ...prev, email: e.target.value.trim() }));
  }

  function onChangePassword(e: ChangeEvent<HTMLInputElement>) {
    setIsErrorShown(false);
    setCredentials((prev) => ({ ...prev, password: e.target.value.trim() }));
  }

  return (
    <div className={"flex flex-col items-center h-[calc(100vh - 40px)]"}>
      <LogoSvg className={"my-[64px]"} />
      <div className={"max-w-[400px] w-full"}>
        <div>
          <div className={"flex mb-[32px] flex-col items-center"}>
            <h5 className={"text-[32px] leading-[36px]"}>Log In</h5>
          </div>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              getLoginAccess();
            }}
          >
            <div className={"flex flex-col  gap-[16px]"}>
              <div className={"flex flex-col gap-[4px]"}>
                <label className={"text-inLabel font-medium mb-[4px]"}>Email</label>
                <Input
                  value={credentials.email}
                  onChange={onChangeEmail}
                  onBlur={() => {
                    setEmailError(
                      CredentialsSchema.safeParse(credentials).error?.issues.find((issue) => issue.path[0] === "email")
                        ?.message
                    );
                  }}
                  className={emailError && "invalid"}
                  required
                />
                <p className={"text-errorRed text-xs flex items-center gap-1"}>
                  {emailError && <WarningIcon width={18} height={18} />}
                  {emailError}
                </p>
              </div>
              <div className={"flex flex-col gap-[8px]"}>
                <label className={"text-inLabel font-medium"}>Password</label>
                <div className={"relative"}>
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={credentials.password}
                    onChange={onChangePassword}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-sm font-medium"
                  >
                    {showPassword ? "Hide" : "Show"}
                  </button>
                </div>
              </div>
              <div className={"flex text-right mb-4"}>
                <Link to={"/forgot-password"}>
                  <Button variant="ghost" className={"p-0"} type="button">
                    Forgot Password?
                  </Button>
                </Link>
                <br />
              </div>
            </div>
            <Button variant={"innoveraPrimary"} className={"w-full"} disabled={mutation.isPending} type="submit">
              Log In
            </Button>

            <p className={"text-errorRed text-xs pt-4"}>{isErrorShown && loginErrorMessage}</p>
          </form>
        </div>
      </div>
    </div>
  );
}
